import { type BigMacDevice } from '@m/types/api';
import { apiFetchV2 } from '@m/utils/http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getLocationLaneDevicesQueryKey } from './query-keys';
import { adminSiteDevice, adminSiteDevices, ehcsDeviceUpdate } from './routes';

type LaneDeviceMutationVariables = {
  body: BigMacDevice;
  deviceUuid: string;
  siteId: number;
};

type UpdateLaneDeviceMutationVariables = {
  body: BigMacDevice;
};

type AbandonLaneDeviceMutationVariables = {
  deviceId: string;
  siteId: number;
};

const getLaneDevices = ({ locationId }: { locationId: number }) =>
  apiFetchV2<BigMacDevice[]>(adminSiteDevices(locationId), {
    method: 'GET',
  });

const adoptLaneDevice = ({ siteId, deviceUuid, body }: LaneDeviceMutationVariables) =>
  apiFetchV2(adminSiteDevice(siteId, deviceUuid), {
    method: 'PUT',
    body,
  });

const updateLaneDevice = ({ body }: UpdateLaneDeviceMutationVariables) =>
  apiFetchV2(ehcsDeviceUpdate(body.uuid), {
    method: 'PUT',
    body,
  });

const abandonLaneDevice = ({ siteId, deviceId }: AbandonLaneDeviceMutationVariables) =>
  apiFetchV2(adminSiteDevice(siteId, deviceId), {
    method: 'DELETE',
  });

export const useLocationLaneDevices = (locationId: number) =>
  useQuery({
    queryKey: getLocationLaneDevicesQueryKey(locationId),
    queryFn: () => getLaneDevices({ locationId }),
    enabled: !!locationId,
  });

export const useAdoptLaneDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: adoptLaneDevice,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getLocationLaneDevicesQueryKey(variables.siteId),
      });
    },
  });
};

export const useUpdateLaneDevice = () => useMutation({ mutationFn: updateLaneDevice });

export const useAbandonLaneDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: abandonLaneDevice,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getLocationLaneDevicesQueryKey(variables.siteId),
      });
    },
  });
};
